import { useState } from 'react';
import { FORM_LABEL_CLASS, FORM_TEXTAREA_CLASS, Modal } from '@safetyhub/ui';
import { EditModalFooter } from '../common/EditModalFooter';
import {
  EXCLUSION_REASON_CODES,
  EXCLUSION_REASON_LABEL,
  type ExclusionReasonCode,
} from '../../lib/legalDocumentLabels';

/** 서버로 보내는 메모 상한 — 화면 쪽 안전선(서버 DTO가 정본이다) */
const NOTE_MAX_LENGTH = 500;

export interface ExclusionModalProps {
  open: boolean;
  /** 제외할 의무 제목 — 무엇을 제외하는지 모달 안에서 다시 보이게 한다 */
  obligationTitle: string;
  /** 등록 진행 중 — 닫기·재제출을 잠근다 */
  submitting: boolean;
  /** 서버 거부 문구(없으면 null) — 모달을 연 채로 보여 준다(닫으면 무엇이 틀렸는지 잃는다) */
  errorMessage: string | null;
  onSubmit: (input: { reasonCode: ExclusionReasonCode; note?: string }) => void;
  onClose: () => void;
}

/**
 * 법정 의무 「해당 없음」 등록 — 사업장의 **구조적** 제외(L11)다.
 *
 * ⚠️ 「이번 기간에 사건이 없었다」(L13)는 제외가 아니다 — 그쪽은 기간별 증빙이고 이행으로 센다
 * (서버 `SiteObligationExclusionService` doc). 그래서 사유 선택지마다 그 경계를 문구로 적는다.
 * 사유는 닫힌 분류뿐이고 자유 입력은 「메모」다(분류 밖 사유를 받는 칸이 없는 것이 서버 enum의 결정).
 *
 * 사용 예:
 * ```tsx
 * <ExclusionModal open obligationTitle="안전검사" submitting={false} errorMessage={null}
 *   onSubmit={({ reasonCode, note }) => …} onClose={…} />
 * ```
 */
export default function ExclusionModal({
  open,
  obligationTitle,
  submitting,
  errorMessage,
  onSubmit,
  onClose,
}: ExclusionModalProps) {
  const [reasonCode, setReasonCode] = useState<ExclusionReasonCode | null>(null);
  const [note, setNote] = useState('');
  const isDirty = reasonCode !== null || note.trim() !== '';

  const handleSubmit = () => {
    if (!reasonCode || submitting) return;
    const trimmed = note.trim();
    onSubmit({ reasonCode, ...(trimmed ? { note: trimmed } : {}) });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="해당 없음으로 제외"
      size="md"
      closeLocked={submitting}
      isDirty={isDirty}
      footer={
        // 취소가 dirty 닫기 확인을 타게 하는 규약 푸터(`useModalRequestClose` — 그 컴포넌트 doc)
        <EditModalFooter
          isDirty={isDirty}
          blockedReason={reasonCode ? '' : '사유를 선택하세요.'}
          submitLabel="제외 등록"
          submitDisabled={!reasonCode || submitting}
          busy={submitting}
          busyLabel="등록 중..."
          onSubmit={handleSubmit}
          onFallbackClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          <span className="font-bold text-ink-900">{obligationTitle}</span> 문서를 이 현장에서
          제외합니다. 제외 기록은 사유와 함께 남고, 해제하면 다시 문서 목록의 관리 대상이 됩니다.
        </p>

        <fieldset>
          <legend className={FORM_LABEL_CLASS}>사유</legend>
          <div className="space-y-2">
            {EXCLUSION_REASON_CODES.map((code) => {
              const { label, hint } = EXCLUSION_REASON_LABEL[code];
              const checked = reasonCode === code;
              return (
                <label
                  key={code}
                  className={`flex cursor-pointer items-start gap-3 rounded-[10px] border px-3 py-2.5 ${
                    checked ? 'border-brand-500 bg-brand-50' : 'border-line hover:bg-surface-hover'
                  }`}
                >
                  <input
                    type="radio"
                    name="legal-exclusion-reason"
                    value={code}
                    checked={checked}
                    onChange={() => setReasonCode(code)}
                    disabled={submitting}
                    className="mt-1 accent-brand-600"
                  />
                  <span>
                    <span className="block text-sm font-bold text-ink-900">{label}</span>
                    <span className="block text-xs text-ink-500">{hint}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <div>
          <label htmlFor="legal-exclusion-note" className={FORM_LABEL_CLASS}>
            메모 (선택)
          </label>
          <textarea
            id="legal-exclusion-note"
            className={FORM_TEXTAREA_CLASS}
            value={note}
            maxLength={NOTE_MAX_LENGTH}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
            placeholder="예: 해당 설비를 2025년에 매각했습니다."
          />
        </div>

        {errorMessage && (
          <p
            role="alert"
            className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
